import { graphql } from 'babel-plugin-relay/macro'
import React, { Suspense } from 'react'
import { useLazyLoadQuery } from 'react-relay-offline'
import {
  ProductCarousel,
  ProductCarouselDescription,
  ProductCarouselHeader,
  ProductCarouselTitle,
} from '@/checkout-instore-legacy/components/ProductCarousel'
import { useBreakpoint } from '@vtex/admin-ui'

import type { CollectionsShelfQuery } from './__generated__/CollectionsShelfQuery.graphql'
import { CollectionsShelfList } from './CollectionsShelfList'
import { CollectionsShelfSkeleton } from './CollectionsShelfSkeleton'
import { logger } from '../../../../../../logger'
import { CollectionsShelfErrorState } from './CollectionsShelfErrorState'
import { CollectionsShelfEmptyState } from './CollectionsShelfEmptyState'
import { ErrorBoundary } from '../../../../../../core/components'
import { getSimulationAddresses } from '../../../../../../core/utils/simulation'

export type CollectionsShelfContentProps = {
  title: string
  description?: string
  collectionId: string
}

export type CollectionsShelfProps = CollectionsShelfContentProps

const CollectionsShelfContent = ({
  collectionId,
  description,
  title,
}: CollectionsShelfContentProps) => {
  const { breakpoint } = useBreakpoint()
  const itemsPerPage = breakpoint === 'tablet' ? 6 : 4
  const simulationAddresses = getSimulationAddresses()

  const { data, error } = useLazyLoadQuery<CollectionsShelfQuery>(
    graphql`
      query CollectionsShelfQuery(
        $collectionId: ID!
        $count: Int
        $addresses: [SimulationAddressInput!]!
      ) {
        collection(id: $collectionId) {
          ...CollectionsShelfList_Collection @arguments(count: $count)
        }
      }
    `,
    {
      collectionId,
      count: itemsPerPage,
      addresses: simulationAddresses,
    },
    {
      fetchPolicy: 'store-and-network',
      skip: !collectionId || !simulationAddresses.length,
    }
  )

  if (error) {
    logger.error({
      workflowType: 'collections-shelf',
      error: error instanceof Error ? error : new Error(String(error)),
    })

    return <CollectionsShelfErrorState error={error} />
  }

  if (!data?.collection) {
    return (
      <>
        <ProductCarouselHeader>
          <ProductCarouselTitle>{title}</ProductCarouselTitle>
          <ProductCarouselDescription>{description}</ProductCarouselDescription>
        </ProductCarouselHeader>
        <CollectionsShelfEmptyState />
      </>
    )
  }

  return (
    <ProductCarousel>
      <ProductCarouselHeader>
        <ProductCarouselTitle>{title}</ProductCarouselTitle>
        <ProductCarouselDescription>{description}</ProductCarouselDescription>
      </ProductCarouselHeader>
      <CollectionsShelfList
        connection={data.collection}
        itemsPerPage={itemsPerPage}
      />
    </ProductCarousel>
  )
}

export const CollectionsShelf = ({
  collectionId,
  title,
  description,
}: CollectionsShelfProps) => {
  return (
    <ErrorBoundary
      fallback={(props, errorId) => {
        return (
          <>
            <ProductCarouselHeader>
              <ProductCarouselTitle>{title}</ProductCarouselTitle>
              <ProductCarouselDescription>
                {description}
              </ProductCarouselDescription>
            </ProductCarouselHeader>
            <CollectionsShelfErrorState error={props} errorId={errorId} />
          </>
        )
      }}
    >
      <Suspense fallback={<CollectionsShelfSkeleton />}>
        <CollectionsShelfContent
          collectionId={collectionId}
          title={title}
          description={description}
        />
      </Suspense>
    </ErrorBoundary>
  )
}
